/**
 * A aba Outros Bancos: os cartões de folha dos favorecidos de conta apartada.
 *
 * Papel: reproduz `#tab-outrosbancos` e `renderOutrosBancos` (l. 496–556,
 * 3581–3616): o cabeçalho "Pagamento por Outros Bancos" com as setas do mês, e
 * a grade de duas células — a coluna dos cartões de folha, com o painel "+ Novo
 * colaborador" no fim, e o cartão "Novo Lançamento" —, seguida da tabela
 * "Lançamentos do mês (Outros Bancos)". Nada desta aba entra no saldo do C6.
 *
 * Como usar: é o painel que a página põe na aba Outros Bancos, com o mesmo mês
 * aberto do Fechamento e de Lançamentos; as setas daqui chamam `onMudarMes`, e
 * as três abas andam juntas. Os cartões leem a situação de `useOutrosBancos` e o
 * cadastro de `useFavorecidos`, e só aparece o cartão do favorecido que está
 * nas duas respostas. Quem só tem `ver` não vê o cartão "Novo Lançamento", e a
 * coluna dos cartões de folha ocupa a grade inteira.
 *
 * Sem favorecido de conta apartada, a coluna mostra um cartão só, com a sigla
 * de `siglaDaFolhaSemColaborador` no título e o "+ Novo colaborador", para a
 * primeira pessoa poder entrar na folha por aqui. O painel "+ Novo
 * colaborador" monta assim que o cadastro chega, que não depende do mês: trocar
 * para um mês ainda não carregado não o desmonta, e o texto digitado continua.
 *
 * As duas importações em PDF dos cartões — salário e guia DAE — passam por uma
 * seleção de arquivo só de cada tipo, que guarda o favorecido do cartão que a
 * abriu. O salário é procurado no PDF pelo CPF do cadastro, pelo nome e, com
 * um funcionário só, por ele; sem achar, o aviso não diz nome nenhum. A
 * leitura que falha avisa "Erro ao ler o PDF: " seguido da mensagem do pdf.js.
 * O PDF do salário só é lido com o mapa CPF→nome de `useNomesDaFolha` já
 * chegado: antes, a escolha avisa "Aguarde os nomes da folha carregarem" sem
 * ler nada e, se a consulta falhou, pede o mapa de novo — sem o mapa, o nome
 * do cadastro que não tem CPF não casaria com o do PDF.
 */
import { type Favorecido, type SituacaoDoMes, acessoAlcanca } from '@concierge/shared';
import { useRef, useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { useAcesso } from '@/shared/hooks';
import { extrairTextoDoPdf } from '@/shared/lib/pdf';
import { useCasaDoCaixa, useFavorecidos } from '../hooks/usar-financas';
import {
  useConfiguracao,
  useNomesDaFolha,
  useOutrosBancos,
} from '../hooks/usar-financas-extras';
import { acharFuncionario, lerFolhaDoPdf } from '../importacao/folha-pdf';
import { type GuiaDoPdf, lerGuiaDae } from '../importacao/guia-dae';
import { favorecidosDaFolha, mesPorExtenso } from '../outros-bancos';
import { CartaoDeFolha } from './cartao-de-folha';
import { CartaoNovoLancamento } from './cartao-novo-lancamento';
import { ConferenciaDaGuiaDeOutrosBancos } from './conferencia-da-guia-de-outros-bancos';
import { ConferenciaDoSalario } from './conferencia-do-salario';
import { Botao } from './kit/botao';
import { CabecalhoDoCartao, Cartao, CorpoDoCartao, TituloDoCartao } from './kit/cartao';
import { EntradaDeArquivo } from './kit/entrada-de-arquivo';
import { SetasDoMes } from './kit/setas-do-mes';
import { PainelNovoColaborador } from './painel-novo-colaborador';
import { TabelaDeOutrosBancos } from './tabela-de-outros-bancos';

interface AbaOutrosBancosProps {
  /** O mês aberto, em `AAAA-MM`. */
  competencia: string;
  /** Anda o mês aberto: -1 pela seta ‹, +1 pela ›. */
  onMudarMes: (meses: number) => void;
}

/** A caixa de conferência aberta, com o favorecido do cartão e o que foi lido. */
type ConferenciaAberta =
  | {
      tipo: 'salario';
      favorecido: Favorecido;
      liquido: number;
      competencia: string | null;
    }
  | { tipo: 'guia'; favorecido: Favorecido; guia: GuiaDoPdf };

/**
 * O texto de uma falha de leitura.
 *
 * @param erro - O que o pdf.js lançou.
 * @returns A mensagem do erro.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : String(erro);
}

/** Um favorecido de conta apartada com a situação do mês dele. */
interface CartaoDoMes {
  favorecido: Favorecido;
  situacao: SituacaoDoMes;
}

/**
 * Junta o cadastro à situação do mês.
 *
 * @param favorecidos - Os favorecidos da casa.
 * @param situacoes - As situações que a API devolveu.
 * @returns Um item por favorecido de conta apartada que tem situação, na ordem
 *   de `favorecidosDaFolha`; o que ainda não voltou numa das duas consultas
 *   fica de fora até voltar.
 */
function cartoesDoMes(
  favorecidos: readonly Favorecido[],
  situacoes: readonly SituacaoDoMes[],
): CartaoDoMes[] {
  const porFavorecido = new Map(
    situacoes.map((situacao) => [situacao.favorecidoId, situacao]),
  );
  return favorecidosDaFolha(favorecidos).flatMap((favorecido) => {
    const situacao = porFavorecido.get(favorecido.id);
    return situacao === undefined ? [] : [{ favorecido, situacao }];
  });
}

/**
 * A aba Outros Bancos.
 *
 * @param competencia - O mês aberto.
 * @param onMudarMes - Anda o mês aberto da página inteira.
 * @returns O cabeçalho, a grade e a tabela do mês.
 */
export function AbaOutrosBancos({ competencia, onMudarMes }: AbaOutrosBancosProps) {
  const casa = useCasaDoCaixa();
  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'financas', 'editar');
  const consulta =
    casa.residenciaId === null ? null : { residenciaId: casa.residenciaId, competencia };
  const outrosBancos = useOutrosBancos(consulta);
  const favorecidos = useFavorecidos(casa.residenciaId);
  const configuracao = useConfiguracao(casa.residenciaId);
  const nomes = useNomesDaFolha(casa.residenciaId, podeEditar);
  const notificar = useNotificacao();
  const [novoAberto, setNovoAberto] = useState(false);
  const [conferencia, setConferencia] = useState<ConferenciaAberta | null>(null);
  const arquivoDoSalario = useRef<HTMLInputElement>(null);
  const arquivoDaGuia = useRef<HTMLInputElement>(null);
  const favorecidoDoArquivo = useRef<Favorecido | null>(null);

  const carregado = outrosBancos.data !== undefined && favorecidos.data !== undefined;
  const falhou = outrosBancos.isError || favorecidos.isError;
  const cartoes = carregado ? cartoesDoMes(favorecidos.data, outrosBancos.data.situacoes) : [];

  /** "Tentar de novo": pede outra vez as duas consultas. */
  function handleTentarDeNovo() {
    void outrosBancos.refetch();
    void favorecidos.refetch();
  }

  /**
   * Abre a seleção de arquivo de um tipo para o cartão de um favorecido.
   *
   * @param entrada - A seleção do salário ou a da guia.
   * @param favorecido - O favorecido do cartão.
   */
  function handlePedirArquivo(
    entrada: { current: HTMLInputElement | null },
    favorecido: Favorecido,
  ) {
    favorecidoDoArquivo.current = favorecido;
    entrada.current?.click();
  }

  /**
   * Lê o PDF do salário e abre a conferência (l. 4671–4708).
   *
   * @param arquivo - O PDF escolhido.
   */
  async function handleSalario(arquivo: File) {
    const favorecido = favorecidoDoArquivo.current;
    if (favorecido === null) return;
    const mapa = nomes.data;
    if (mapa === undefined) {
      if (nomes.isError) void nomes.refetch();
      notificar('Aguarde os nomes da folha carregarem', false);
      return;
    }
    let folha: ReturnType<typeof lerFolhaDoPdf>;
    try {
      folha = lerFolhaDoPdf(await extrairTextoDoPdf(arquivo), mapa);
    } catch (erro) {
      notificar(`Erro ao ler o PDF: ${mensagemDe(erro)}`, false);
      return;
    }
    if (folha.funcionarios.length === 0) {
      notificar(
        'Não foi possível identificar o salário líquido no PDF. Confira o arquivo.',
        false,
      );
      return;
    }
    const funcionario = acharFuncionario(folha.funcionarios, favorecido);
    if (funcionario === null) {
      notificar('Nome não encontrado no PDF', false);
      return;
    }
    setConferencia({
      tipo: 'salario',
      favorecido,
      liquido: funcionario.liquido,
      competencia: folha.competenciaDetectada ? folha.competencia : null,
    });
  }

  /**
   * Lê o PDF da guia DAE e abre a conferência (l. 4566–4584).
   *
   * @param arquivo - O PDF escolhido.
   */
  async function handleGuia(arquivo: File) {
    const favorecido = favorecidoDoArquivo.current;
    if (favorecido === null) return;
    let guia: GuiaDoPdf;
    try {
      guia = lerGuiaDae(await extrairTextoDoPdf(arquivo));
    } catch (erro) {
      notificar(`Erro ao ler o PDF: ${mensagemDe(erro)}`, false);
      return;
    }
    if (guia.itens.length === 0) {
      notificar(
        'Não foi possível identificar as linhas da guia no PDF. Confira o arquivo.',
        false,
      );
      return;
    }
    setConferencia({ tipo: 'guia', favorecido, guia });
  }

  /** O "+ Novo colaborador" de qualquer cartão abre e fecha o mesmo painel. */
  function handleNovoColaborador() {
    setNovoAberto((aberto) => !aberto);
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
        <div>
          <div className="font-outfit text-[15px] font-light tracking-[0.08em] text-foreground uppercase">
            Pagamento por Outros Bancos
          </div>
          <div className="mt-0.5 text-[11px] text-texto-fraco">
            Favorecidos marcados no cadastro como &quot;conta apartada&quot; — fora do fluxo C6
          </div>
        </div>
        <SetasDoMes
          rotulo={mesPorExtenso(competencia)}
          onAnterior={() => onMudarMes(-1)}
          onProximo={() => onMudarMes(1)}
        />
      </div>

      <div className="mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-stretch gap-4">
        <div className="flex flex-col gap-3.5">
          {!carregado ? (
            <div className="p-6 text-center text-[13px] text-texto-fraco">
              {falhou ? (
                <>
                  Não foi possível carregar.{' '}
                  <Botao variante="fantasma" tamanho="sm" onClick={handleTentarDeNovo}>
                    Tentar de novo
                  </Botao>
                </>
              ) : (
                'Carregando…'
              )}
            </div>
          ) : cartoes.length === 0 ? (
            <Cartao>
              <CabecalhoDoCartao>
                <TituloDoCartao className="text-texto-fraco">
                  {`Folha de Pagamento ${configuracao.data?.siglaDaFolhaSemColaborador ?? ''}`.trimEnd()}
                </TituloDoCartao>
                {podeEditar && (
                  <Botao variante="fantasma" tamanho="sm" onClick={handleNovoColaborador}>
                    + Novo colaborador
                  </Botao>
                )}
              </CabecalhoDoCartao>
              <CorpoDoCartao className="p-6 text-center text-[13px] text-texto-fraco">
                Nenhum favorecido marcado como &quot;Pagamento por conta apartada&quot; no
                cadastro.
              </CorpoDoCartao>
            </Cartao>
          ) : (
            cartoes.map(({ favorecido, situacao }) => (
              <CartaoDeFolha
                key={favorecido.id}
                favorecido={favorecido}
                situacao={situacao}
                fechada={outrosBancos.data?.folhasFechadas.find(
                  (folha) => folha.favorecidoId === favorecido.id,
                )}
                competencia={competencia}
                podeEditar={podeEditar}
                onNovoColaborador={handleNovoColaborador}
                onImportarSalario={() => handlePedirArquivo(arquivoDoSalario, favorecido)}
                onImportarGuia={() => handlePedirArquivo(arquivoDaGuia, favorecido)}
              />
            ))
          )}
          {podeEditar && favorecidos.data !== undefined && (
            <PainelNovoColaborador
              favorecidos={favorecidos.data}
              aberto={novoAberto}
              onIncluido={() => setNovoAberto(false)}
            />
          )}
        </div>
        {podeEditar && casa.residenciaId !== null && (
          <CartaoNovoLancamento residenciaId={casa.residenciaId} competencia={competencia} />
        )}
      </div>

      <TabelaDeOutrosBancos
        lancamentos={outrosBancos.data?.lancamentos}
        falhou={outrosBancos.isError}
        onTentarDeNovo={() => void outrosBancos.refetch()}
        podeEditar={podeEditar}
      />

      {podeEditar && (
        <>
          <EntradaDeArquivo
            ref={arquivoDoSalario}
            aceita="application/pdf"
            rotulo="PDF do salário"
            onArquivo={(arquivo) => void handleSalario(arquivo)}
          />
          <EntradaDeArquivo
            ref={arquivoDaGuia}
            aceita="application/pdf"
            rotulo="PDF da guia DAE do cartão"
            onArquivo={(arquivo) => void handleGuia(arquivo)}
          />
        </>
      )}
      {conferencia?.tipo === 'salario' && (
        <ConferenciaDoSalario
          favorecido={conferencia.favorecido}
          liquido={conferencia.liquido}
          competenciaDoPdf={conferencia.competencia}
          mesAberto={competencia}
          onFechar={() => setConferencia(null)}
        />
      )}
      {conferencia?.tipo === 'guia' && (
        <ConferenciaDaGuiaDeOutrosBancos
          guia={conferencia.guia}
          favorecido={conferencia.favorecido}
          competencia={competencia}
          onFechar={() => setConferencia(null)}
        />
      )}
    </div>
  );
}
